import type { ReactNode } from "react";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRoute,
} from "@tanstack/react-router";
import { PostHogProvider } from "@posthog/react";

import { jsonLd, organizationJsonLd, seoMeta, webSiteJsonLd } from "../seo";
import appCss from "../styles.css?url";

// PostHog options per the official guide. `apiKey`+`options` makes the provider
// initialize the SDK inside a client effect, which is safe for SSR on Workers.
const posthogOptions = {
  api_host: import.meta.env.VITE_POSTHOG_HOST ?? "https://g.throughpilot.com",
  disable_surveys: true,
  defaults: "2026-01-30",
} as const;

function AnalyticsProvider({ children }: { children: ReactNode }) {
  const token = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
  if (!token) return <>{children}</>;
  return (
    <PostHogProvider apiKey={token} options={posthogOptions}>
      {children}
    </PostHogProvider>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      { name: "theme-color", content: "#0b0b0d" },
      ...seoMeta({
        title: "ThroughPilot - Senior Product Engineering for AI-era builds",
        description:
          "Lovable, Replit, and AI-era product builds taken through to production with senior full-stack engineering.",
        path: "/",
      }),
    ],
    links: [
      {
        rel: "preload",
        href: "/fonts/space-grotesk-700.ttf",
        as: "font",
        type: "font/ttf",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        href: "/fonts/instrument-serif-italic-400.ttf",
        as: "font",
        type: "font/ttf",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
      {
        rel: "icon",
        type: "image/svg+xml",
        href: "/brand/icon.svg",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/brand/favicon-32.png",
      },
      {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/brand/favicon-16.png",
      },
      {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/brand/apple-touch-icon.png",
      },
      {
        rel: "manifest",
        href: "/manifest.json",
      },
    ],
    scripts: [jsonLd(organizationJsonLd()), jsonLd(webSiteJsonLd())],
  }),
  component: RootComponent,
});

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  );
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <html
      lang="en"
      data-theme="lavender"
      data-backdrop="flow"
      data-hide-svcs=""
    >
      <head>
        <HeadContent />
      </head>
      <body>
        <AnalyticsProvider>{children}</AnalyticsProvider>
        <Scripts />
      </body>
    </html>
  );
}
